import React, { useState } from "react";
import {Mail, Phone, MapPin, Send, MessageCircleDashed as MessageCircle} from 'lucide-react';
import toast, { Toaster } from "react-hot-toast";
import { lumi } from "../lib/lumi";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.eventType) {
      toast.error("Пожалуйста, заполните все обязательные поля");
      return;
    }

    setIsSubmitting(true);

    try {
      const now = new Date().toISOString();

      // Сохранение заявки в БД
      await lumi.entities.contact_requests.create({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "",
        eventType: formData.eventType,
        eventDate: formData.eventDate || "",
        message: formData.message || "",
        status: "new",
        creator: "website",
        createdAt: now,
        updatedAt: now
      });

      toast.success("Спасибо! Заявка отправлена. Я свяжусь с вами в течение 24 часов.");

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        message: ""
      });
    } catch (error) {
      console.error("Ошибка отправки заявки:", error);
      toast.error("Произошла ошибка при отправке заявки. Попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-black">
      <Toaster position="top-center" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Свяжитесь со мной</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Готовы сделать ваше мероприятие незабываемым? Давайте обсудим ваше видение и воплотим его в жизнь вместе.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gray-900 border border-pink-300/20 rounded-3xl p-8 md:p-10">
            <h3 className="text-2xl font-bold text-white mb-6">Отправить сообщение</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Полное имя *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-pink-300/30 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent outline-none transition text-white"
                  placeholder="Иван Иванов" />

              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email адрес *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-pink-300/30 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent outline-none transition text-white"
                  placeholder="ivan@example.com" />

              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Номер телефона
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black border border-pink-300/30 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent outline-none transition text-white"
                  placeholder="+7 (999) 123-4567" />

              </div>

              <div>
                <label htmlFor="eventType" className="block text-sm font-medium text-gray-300 mb-2">
                  Тип мероприятия *
                </label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-pink-300/30 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent outline-none transition text-white">

                  <option value="">Выберите тип мероприятия</option>
                  <option value="newyear">Новогодний корпоратив</option>
                  <option value="wedding">Свадебная вечеринка</option>
                  <option value="mafia">Мафия-вечеринка</option>
                  <option value="gender">Гендер-пати</option>
                  <option value="sports">Спортивное мероприятие</option>
                  <option value="custom">Индивидуальный формат</option>
                  <option value="other">Другое</option>
                </select>
              </div>

              <div>
                <label htmlFor="eventDate" className="block text-sm font-medium text-gray-300 mb-2">
                  Дата мероприятия
                </label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black border border-pink-300/30 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent outline-none transition text-white" />

              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Сообщение
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-black border border-pink-300/30 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent outline-none transition resize-none text-white"
                  placeholder="Расскажите о вашем мероприятии..." />

              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-pink-300 text-black py-4 rounded-lg font-semibold hover:bg-pink-400 transition shadow-lg shadow-pink-300/20 hover:shadow-pink-300/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">

                <Send className="w-5 h-5 mr-2" />
                {isSubmitting ? "Отправка..." : "Отправить сообщение"}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Контактная информация</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-pink-300/20 border border-pink-300/30 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-pink-300" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-white mb-1">Телефон</h4>
                    <p className="text-gray-300">+7 (905) 728-88-21</p>
                    <p className="text-sm text-gray-400">Пн-Пт 9:00-18:00 МСК</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-pink-300/20 border border-pink-300/30 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-pink-300" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-white mb-1">Email</h4>
                    <p className="text-gray-300">m5m@ya.ru</p>
                    <p className="text-sm text-gray-400">Пишите круглосуточно</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-pink-300/20 border border-pink-300/30 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-pink-300" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-white mb-1">Местоположение</h4>
                    <p className="text-gray-300">Москва, Россия</p>
                    <p className="text-sm text-gray-400">Работаю по ЦАО\СВАО г. Москва</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-gradient-to-br from-pink-300/10 to-pink-400/10 border border-pink-300/30 rounded-3xl p-8">
              <h4 className="font-bold text-white mb-4">Следите за мной в соцсетях</h4>
              <div className="flex gap-4">
                <a
                  href="https://t.me/princesarc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black border border-pink-300/30 p-3 rounded-full hover:bg-pink-300 hover:text-black transition shadow-md hover:shadow-lg hover:shadow-pink-300/30 text-pink-300"
                  aria-label="Telegram">

                  <MessageCircle className="w-6 h-6" />
                </a>
                <a
                  href="https://vk.com/id278147696"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black border border-pink-300/30 p-3 rounded-full hover:bg-pink-300 hover:text-black transition shadow-md hover:shadow-lg hover:shadow-pink-300/30 text-pink-300"
                  aria-label="ВКонтакте">

                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2zm3.45 15.39h-1.31c-.77 0-1-.61-2.37-2-.89-.89-1.28-1-1.5-1-.31 0-.4.09-.4.52v1.82c0 .49-.16.78-1.42.78-2.09 0-4.4-1.26-6.03-3.62-2.45-3.34-3.12-5.85-3.12-6.37 0-.22.09-.43.52-.43h1.31c.39 0 .54.18.69.59.77 2.2 2.07 4.13 2.6 4.13.2 0 .29-.09.29-.59v-2.28c-.07-1.13-.66-1.22-.66-1.62 0-.18.15-.35.38-.35h2.06c.33 0 .44.18.44.56v3.08c0 .33.15.44.24.44.2 0 .36-.11.73-.48 1.13-1.26 1.94-3.22 1.94-3.22.11-.22.28-.43.7-.43h1.31c.46 0 .56.24.46.56-.17.8-1.93 3.35-1.93 3.35-.16.27-.22.39 0 .69.15.22.64.63 1 1 .68.75 1.21 1.38 1.35 1.82.14.43-.08.65-.51.65z" />
                  </svg>
                </a>
                <a
                  href="https://wa.me/79057288821"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black border border-pink-300/30 p-3 rounded-full hover:bg-pink-300 hover:text-black transition shadow-md hover:shadow-lg hover:shadow-pink-300/30 text-pink-300"
                  aria-label="WhatsApp">

                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Availability Notice */}
            <div className="bg-gray-900 border border-pink-300/20 text-white rounded-3xl p-8">
              <h4 className="font-bold text-xl mb-3 text-pink-300">Бронируйте заранее!</h4>
              <p className="text-gray-300 leading-relaxed">
                Популярные даты быстро заполняются, особенно в новогодний и свадебный сезон (май-октябрь). 
                Свяжитесь со мной сегодня, чтобы проверить доступность на вашу дату.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

export default Contact;